import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "How fast can I actually get funded?",
    a: "Many of our funding products can deliver money in as little as 24 hours from approval. Working capital and merchant cash advances are the fastest; SBA loans take longer (a few weeks) because of the government process.",
  },
  {
    q: "What credit score do I need?",
    a: "We fund businesses with scores as low as 500 for revenue-based products. Better credit unlocks better rates, but we look at the whole picture — cash flow, tenure, and industry — not just a number.",
  },
  {
    q: "Does applying hurt my credit?",
    a: "No. Our initial application is a soft pull only. We won't do a hard credit inquiry until you accept an offer and want to move forward.",
  },
  {
    q: "How much can I qualify for?",
    a: "It depends on your monthly revenue and time in business. As a rule of thumb, most businesses qualify for 3–8 months of average monthly revenue. Use the calculator on this page for an instant estimate.",
  },
  {
    q: "What documents do I need?",
    a: "For most fast-funding products: 3 months of business bank statements and a completed one-page application. SBA and larger deals require additional financials.",
  },
  {
    q: "Are there any upfront or hidden fees?",
    a: "Never. There is no cost to apply, no cost to see your options, and every fee is disclosed in writing before you sign.",
  },
  {
    q: "Can I get funded if I've been denied by a bank?",
    a: "Yes — many of our clients have been declined by traditional banks. We look at real business performance, not just rigid bank criteria.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-purple-600">
            FAQ
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 tracking-tight text-slate-900">
            Answers to the big questions.
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((f, idx) => {
            const isOpen = open === idx;
            return (
              <div
                key={f.q}
                data-testid={`faq-item-${idx}`}
                className={`rounded-2xl border transition-all ${
                  isOpen
                    ? "bg-purple-50/70 border-purple-200"
                    : "bg-white border-slate-100 hover:border-slate-200"
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : idx)}
                  data-testid={`faq-toggle-${idx}`}
                  className="w-full flex items-center justify-between text-left px-6 py-5"
                >
                  <span className="font-display text-base md:text-lg font-bold text-slate-900 pr-6">
                    {f.q}
                  </span>
                  <span
                    className={`flex-shrink-0 w-8 h-8 rounded-full grid place-items-center transition ${
                      isOpen
                        ? "bg-purple-600 text-white rotate-180"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-slate-600 leading-relaxed">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
